import React, { useState, useEffect } from 'react';
import API from '../services/api';

const EMPTY = { name: '', email: '', password: '', role_id: '3' };

const ROLES = [
  { id: 1, label: 'Super Admin' },
  { id: 2, label: 'Admin'       },
  { id: 3, label: 'Teacher'     },
];

const ROLE_CSS = {
  1: 'bg-yellow-100 text-yellow-700',
  2: 'bg-blue-100 text-blue-700',
  3: 'bg-green-100 text-green-700',
};

export default function UsersPage() {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');

  const load = () => {
    setLoading(true);
    API.get('/users')
      .then(res => setUsers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(EMPTY);
    setEditUser(null);
    setShowForm(true);
    setMsg('');
  };

  const openEdit = (user) => {
    setForm({
      name:     user.name    || '',
      email:    user.email   || '',
      password: '',
      role_id:  String(user.role_id),
    });
    setEditUser(user);
    setShowForm(true);
    setMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setMsg('❌ නම සහ Email අවශ්‍යයි');
      return;
    }
    if (!editUser && !form.password) {
      setMsg('❌ නව user ට password අවශ්‍යයි');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name:    form.name,
        email:   form.email,
        role_id: Number(form.role_id),
      };
      if (form.password) payload.password = form.password;

      if (editUser) {
        await API.put(`/users/${editUser.id}`, payload);
        setMsg('✅ පරිශීලකයා යාවත්කාලීන කරන ලදී');
      } else {
        await API.post('/users', payload);
        setMsg('✅ පරිශීලකයා සාර්ථකව එකතු කරන ලදී');
      }
      load();
      setTimeout(() => { setShowForm(false); setMsg(''); }, 1200);
    } catch (err) {
      setMsg('❌ ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" මකා දැමීමට අවශ්‍යද?`)) return;
    try {
      await API.delete(`/users/${id}`);
      load();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  };

  const getRoleLabel = (role_id) => ROLES.find(r => r.id === role_id)?.label || role_id;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">පරිශීලකයන්</h1>
          <p className="text-gray-500 text-sm mt-1">System users කළමනාකරණය</p>
        </div>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/>
          </svg>
          පරිශීලකයා එකතු කරන්න
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="bg-red-500 rounded-t-2xl px-6 py-4 flex items-center justify-between">
              <h2 className="text-white font-semibold">
                {editUser ? 'පරිශීලකයා සංස්කරණය' : 'නව පරිශීලකයා'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-white/80 hover:text-white text-xl">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {msg && (
                <div className={`text-sm p-3 rounded-lg ${
                  msg.startsWith('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>{msg}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">නම <span className="text-red-500">*</span></label>
                <input type="text" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  මුරපදය {editUser && <span className="text-gray-400 text-xs">(හිස් = වෙනස් නොවේ)</span>}
                  {!editUser && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input type="password" value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  placeholder={editUser ? '••••••••' : 'මුරපදය ඇතුළත් කරන්න'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">භූමිකාව <span className="text-red-500">*</span></label>
                <select value={form.role_id}
                  onChange={e => setForm(p => ({ ...p, role_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500">
                  {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50 transition">
                  අවලංගු
                </button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-red-500 text-white py-2 rounded-lg text-sm hover:bg-red-600 disabled:opacity-50 transition">
                  {saving ? 'සුරකිමින්...' : 'සුරකින්න'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-16 text-gray-400">පූරණය වෙමින්...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p className="text-4xl mb-3">👥</p>
            <p className="text-sm">පරිශීලකයන් නොමැත</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#', 'නම', 'Email', 'භූමිකාව', 'ක්‍රියා'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u, i) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-400">{i + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{u.name}</td>
                    <td className="px-4 py-3 text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_CSS[u.role_id] || 'bg-gray-100 text-gray-600'}`}>
                        {getRoleLabel(u.role_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button onClick={() => openEdit(u)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium">සංස්කරණය</button>
                        <button onClick={() => handleDelete(u.id, u.name)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium">මකන්න</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
              මුළු පරිශීලකයන්: {users.length}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}